import { updateIntercomUser } from '@arnold/common';
import { useEffect, useState } from 'react';
import { uniq } from 'ramda';
import { useIsAccessTokenValidQuery, UserOrgRole } from '../../../generated/hooks';
import auth from '../../../lib/auth';

interface UseReportLanguageParams {
  initLanguage: string;
  /** The app-wide language currently active (from i18n), used to detect when it falls outside the organization's allowed set. */
  activeLanguage: string;
  organizationLanguageCodes?: string[];
  reportLanguageCode?: string | null;
  organizationPrimaryLanguageCode?: string | null;
  setAllowedLanguages: (languages: string[]) => void;
  setLocalization: (lang: string) => any;
}

export const useReportLanguage = ({
  initLanguage,
  activeLanguage,
  organizationLanguageCodes,
  reportLanguageCode,
  organizationPrimaryLanguageCode,
  setAllowedLanguages,
  setLocalization,
}: UseReportLanguageParams) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>(initLanguage);
  const { data: isAccessTokenValid } = useIsAccessTokenValidQuery();

  useEffect(() => {
    setSelectedLanguage(activeLanguage);
  }, [activeLanguage]);

  useEffect(() => {
    if (!organizationLanguageCodes) {
      return;
    }
    const allowed = uniq([...organizationLanguageCodes, 'en']);
    setAllowedLanguages(allowed);

    if (!allowed.includes(activeLanguage)) {
      if (reportLanguageCode && allowed.includes(reportLanguageCode)) {
        setLocalization(reportLanguageCode);
      } else if (organizationPrimaryLanguageCode && allowed.includes(organizationPrimaryLanguageCode)) {
        setLocalization(organizationPrimaryLanguageCode);
      } else if (allowed.length > 0) {
        setLocalization(allowed[0]);
      }
    }
  }, [
    organizationLanguageCodes,
    activeLanguage,
    reportLanguageCode,
    organizationPrimaryLanguageCode,
    setAllowedLanguages,
    setLocalization,
  ]);

  const changeLanguage = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    if (languageCode) {
      setLocalization(languageCode);

      if (
        isAccessTokenValid?.isAccessTokenValid?.user?.respondents?.find(
          (r) => r.organizationRole === UserOrgRole.OrgAdmin,
        )
      ) {
        updateIntercomUser({
          language_override: languageCode,
          user_hash: auth.getIntercomHash(),
        });
      }
    }
  };

  return {
    selectedLanguage,
    changeLanguage,
    user: isAccessTokenValid?.isAccessTokenValid?.user,
  };
};
